/**
 * Kinematic bicycle model. Not a rigid body, and deliberately so.
 *
 * A rigid-body car needs a solver, a collision mesh with no gaps, and careful
 * tuning to stop it jittering, climbing kerbs or tunnelling. All three of the
 * competitor's reported bugs are rigid-body-under-bad-mesh symptoms. Driving
 * the position directly and reading the surface underneath removes that entire
 * class of failure, at the cost of things this MVP does not have anyway
 * (collisions with other vehicles, deformation, handbrake slides).
 *
 * Dimensions and limits come from `src/core/spec.ts` - i.e. from TPDM Vol 2
 * Ch 2 - so the car turns the way the design vehicle the roads were laid out
 * for actually turns.
 */
import { CAR_GEOMETRY, DESIGN_SPEED_MS, VEHICLE, VEHICLE_DERIVED } from '../core/spec';

export interface CarInput {
  /** 0..1 */
  throttle: number;
  /** 0..1 */
  brake: number;
  /** -1 (left) .. +1 (right) */
  steer: number;
}

export interface CarState {
  x: number;
  z: number;
  /** Surface height under the car, set by groundFollow. */
  y: number;
  /** Yaw, radians. 0 = facing -Z (north), increasing clockwise from above. */
  heading: number;
  /** Signed, m/s. Negative = reversing. */
  speed: number;
  /** Body attitude, set by groundFollow from the surface. */
  pitch: number;
  roll: number;
  /** Front wheel angle actually applied this frame, for rendering the wheels. */
  steerAngle: number;
  /**
   * How long the brake has been held at a standstill. The brake first just
   * holds the car (e.g. at the lights on a hill); after HOLD_DWELL_S the same
   * pedal selects reverse.
   */
  holdTimer: number;
  /** True when the surface sampler found no road under any wheel. */
  offRoad: boolean;
}

/**
 * Tuning. These are the only numbers in the project chosen by feel rather than
 * cited, and they are all rates rather than dimensions - the shape of the car's
 * path is fixed by the TPDM geometry above.
 */
const TUNING = {
  /** m/s^2. ~7 s from rest to the 50 km/h design speed. */
  ACCEL: 2.6,
  /** m/s^2. Comfortable service braking, well short of an emergency stop. */
  BRAKE: 6.0,
  /** m/s. Reverse is slow on purpose; this is a test-route sim, not a rally. */
  MAX_REVERSE: 3.0,
  /**
   * m/s^2 lateral. The steering lock is reduced with speed so that the
   * resulting cornering acceleration never exceeds this - which is what stops
   * a touchscreen car from spinning on its axis at 50 km/h. Physically
   * motivated rather than an arbitrary curve.
   */
  MAX_LATERAL_ACCEL: 4.5,
  /** rad/s. How fast the front wheels can be turned. */
  STEER_RATE: 2.4,
  /** Extra deceleration when off the drivable surface. */
  OFFROAD_DRAG: 4.0,
} as const;

/**
 * Speed-dependent resistance. The first two terms are derived from the design
 * vehicle in spec.ts (a ~1300 kg private car, 2.2 m^2 frontal area, Cd 0.30),
 * not tuned by feel:
 *
 *   rolling   C_RR x g                                  (constant, ~0.118)
 *   aero      1/2 rho Cd A / m x v^2                    (K_AERO x v^2)
 *   engine    ENGINE_BRAKE_AT_DESIGN x v / design speed (linear in v)
 *
 * Engine braking is the ONE knob. It cannot be a constant: "coasts downhill
 * without slowing" wants it <= 0.18 while "lifting off scrubs speed" wants it
 * >= 0.30 - so it scales with speed, which is also how a torque converter
 * actually drags (roughly with rpm). At E = 0.30, verified numerically:
 * parked, holds on <=1.5% and rolls on >=2%; terminal velocity on a 3.6%
 * grade is ~34.5 km/h; at 20 km/h downhill the car ACCELERATES
 * (+0.106 m/s^2); at 50 km/h it eases back (-0.12 m/s^2); lifting off at
 * 50 km/h on the flat loses ~4.9 km/h in 3 s; coast to rest ~56 s.
 */
const RESIST = {
  /** Rolling resistance coefficient, tyre on dry asphalt. */
  C_RR: 0.012,
  /** 1/2 * rho * Cd * A / m: (0.5 * 1.2 * 0.30 * 2.2) / 1300. */
  K_AERO: 3.05e-4,
  /** m/s^2 of engine braking at the 50 km/h design speed. The tuning knob. */
  ENGINE_BRAKE_AT_DESIGN: 0.3,
  /** m/s. Below this the car is treated as at rest, for statics. */
  STATIC_EPS: 0.05,
  /**
   * m/s^2. Static friction breakaway: holds a parked car on <=1.5%, lets it
   * roll on >=2%. Sits cleanly between TPDM's 0.67% drainage minimum and any
   * real gradient, so a compliant road is never ambiguously "flat".
   */
  STATIC_BREAKAWAY: 0.15,
  /** m/s^2 the service brake can hold against. Covers the 10% TPDM maximum (0.98). */
  BRAKE_HOLD: 4.0,
  /** s the brake holds at a standstill before the pedal selects reverse. */
  HOLD_DWELL_S: 0.4,
} as const;

const G = 9.81;

export function createCarState(x: number, z: number, heading: number): CarState {
  return {
    x,
    z,
    y: 0,
    heading,
    speed: 0,
    pitch: 0,
    roll: 0,
    steerAngle: 0,
    holdTimer: 0,
    offRoad: false,
  };
}

/**
 * Advance one step.
 *
 * `grade` is the longitudinal slope under the car (rise/run, positive uphill in
 * the direction of travel). Feeding gravity in through it makes a measured
 * gradient affect driving rather than merely looking like one.
 */
export function stepCar(s: CarState, input: CarInput, dt: number, grade: number): void {
  const v0 = s.speed;
  const atRest = Math.abs(v0) < RESIST.STATIC_EPS;

  // Gravity resolved along the car's forward axis. `grade` is positive when the
  // road rises in the direction the car is FACING, so this term is independent
  // of whether the car is going forwards or backwards - rolling back down a hill
  // in reverse gets the same push, with the correct sign, for free.
  // sin(atan(g)) ~= g at road gradients, but keep it exact: 10% is not a small angle.
  const gravity = -G * Math.sin(Math.atan(grade));

  // --- longitudinal ---
  //
  // Two pedals, no gear selector. Each pedal means "go this way": the down
  // button brakes while moving forwards and then - after a short dwell holding
  // the car still - pulls it backwards, and the up button brakes while
  // reversing and then pulls it forwards. Anything else and holding the brake
  // at a standstill either does nothing or - worse - fights the reverse it
  // just started.
  let accel = 0;
  let brakeHolding = false;

  if (input.brake > 0) {
    if (v0 > RESIST.STATIC_EPS) {
      accel -= TUNING.BRAKE * input.brake; // still rolling forwards: brake
    } else if (v0 < -RESIST.STATIC_EPS) {
      accel -= TUNING.ACCEL * input.brake; // already reversing: keep reversing
    } else {
      // At a standstill. Hold the car first; only a sustained press reverses.
      s.holdTimer += dt;
      if (s.holdTimer > RESIST.HOLD_DWELL_S) {
        accel -= TUNING.ACCEL * input.brake;
      } else {
        brakeHolding = true;
      }
    }
  } else {
    s.holdTimer = 0;
  }

  if (input.throttle > 0) {
    s.holdTimer = 0;
    brakeHolding = false;
    accel +=
      v0 < -RESIST.STATIC_EPS
        ? TUNING.BRAKE * input.throttle // still rolling backwards: brake
        : TUNING.ACCEL * input.throttle; // stopped or moving forwards: drive
  }

  if (brakeHolding) {
    // The service brake holds the car against gravity (up to BRAKE_HOLD; the
    // 10% TPDM maximum needs only 0.98 m/s^2). No creep, no rollback.
    if (Math.abs(gravity) <= RESIST.BRAKE_HOLD * input.brake) {
      s.speed = 0;
      return finishStep(s, input, dt);
    }
    accel += gravity;
  } else if (atRest) {
    if (
      input.throttle <= 0 &&
      input.brake <= 0 &&
      Math.abs(gravity) <= RESIST.STATIC_BREAKAWAY
    ) {
      // Parked with no pedal on a near-flat road: static friction holds. No
      // creeping - and no re-zeroing of a car that is genuinely rolling.
      s.speed = 0;
      return finishStep(s, input, dt);
    }
    // Otherwise only gravity acts at a standstill: every resistive term is
    // speed-dependent and correctly zero at rest. With no pedal and a slope
    // past breakaway the car breaks loose and rolls; with a pedal down the
    // drivetrain breaks static friction on its own.
    accel += gravity;
  } else {
    // Rolling. Resistance opposes the motion: rolling resistance (constant),
    // aero (v^2) and engine braking (linear, and only with the throttle off).
    const dir = Math.sign(v0) || 1;
    let resist = RESIST.C_RR * G * dir + RESIST.K_AERO * v0 * Math.abs(v0);
    if (input.throttle <= 0) {
      resist += RESIST.ENGINE_BRAKE_AT_DESIGN * (v0 / DESIGN_SPEED_MS);
    }
    accel -= resist;
    accel += gravity;

    if (s.offRoad) accel -= TUNING.OFFROAD_DRAG * dir;
  }

  s.speed += accel * dt;

  // Resistance can only remove energy, never reverse the car: if it would take
  // the speed through zero this frame (and neither gravity nor a pedal is
  // driving it there), the car simply stops.
  if (
    v0 !== 0 &&
    Math.sign(s.speed) !== Math.sign(v0) &&
    Math.abs(gravity) < RESIST.STATIC_BREAKAWAY &&
    input.throttle <= 0 &&
    input.brake <= 0
  ) {
    s.speed = 0;
  }

  s.speed = Math.min(DESIGN_SPEED_MS, Math.max(-TUNING.MAX_REVERSE, s.speed));
  finishStep(s, input, dt);
}

/** Steering and integration, shared by every path out of the longitudinal model. */
function finishStep(s: CarState, input: CarInput, dt: number): void {

  // --- steering ---
  const v = Math.abs(s.speed);
  // Lock available at this speed, from the lateral-acceleration budget:
  //   a_lat = v * omega,  omega = v tan(d) / L   =>   d = atan(a_lat L / v^2)
  const lockAtSpeed =
    v < 1
      ? VEHICLE_DERIVED.MAX_STEER_RAD
      : Math.min(
          VEHICLE_DERIVED.MAX_STEER_RAD,
          Math.atan((TUNING.MAX_LATERAL_ACCEL * VEHICLE.WHEELBASE_M) / (v * v)),
        );

  const target = input.steer * lockAtSpeed;
  const maxDelta = TUNING.STEER_RATE * dt;
  s.steerAngle += Math.max(-maxDelta, Math.min(maxDelta, target - s.steerAngle));
  s.steerAngle = Math.max(-lockAtSpeed, Math.min(lockAtSpeed, s.steerAngle));

  // --- integrate ---
  if (Math.abs(s.speed) > 1e-4) {
    const yawRate = (s.speed / VEHICLE.WHEELBASE_M) * Math.tan(s.steerAngle);
    s.heading += yawRate * dt;
  }

  // heading 0 faces -Z (north).
  s.x += Math.sin(s.heading) * s.speed * dt;
  s.z += -Math.cos(s.heading) * s.speed * dt;
}

/** Speed for the HUD. */
export function speedKmh(s: CarState): number {
  return Math.abs(s.speed) * 3.6;
}

/**
 * The four wheel contact points in world XZ, front-left, front-right,
 * rear-left, rear-right.
 *
 * Note: the plan says "four corners of the 4.6 x 1.7 body". These are the wheel
 * contact patches instead - same idea, but the body corners overhang the axles
 * by 1 m front and rear, so using them makes the car start pitching a metre
 * before the wheels reach a crest and report itself off the road while all four
 * wheels are still on tarmac. Attitude has to come from where the rubber is.
 */
const TRACK_M = CAR_GEOMETRY.TRACK_M;

export function wheelPositions(
  s: CarState,
): [[number, number], [number, number], [number, number], [number, number]] {
  const c = Math.cos(s.heading);
  const sn = Math.sin(s.heading);
  const halfWb = VEHICLE.WHEELBASE_M / 2;
  const halfTr = TRACK_M / 2;
  // Forward is (sin h, -cos h); right is (cos h, sin h).
  const pt = (fwd: number, right: number): [number, number] => [
    s.x + sn * fwd + c * right,
    s.z - c * fwd + sn * right,
  ];
  return [pt(halfWb, -halfTr), pt(halfWb, halfTr), pt(-halfWb, -halfTr), pt(-halfWb, halfTr)];
}
